import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { UserItem, UserSearchResponse } from '../type';

import { Grid, Button, SelectBox, Text, Modal, ModalInner } from 'components';
import { ExternalLinkOutlineIcon, BadgeCheckOutlineIcon, XCircleOutlineIcon } from 'assets/svgs';
import { roleList, userPermissionGridColumns } from '../utils'; // 방금 만든 컬럼
import UserDetailModal from './UserDetailModal';
import axios from 'common/AxiosAuth';
import { RootState } from 'store';

// * Status 스타일 정의
const userStatusStyles = {
  ACTIVE: { title: 'Active', className: 'bg-[#E6FEED] border-[#27B72D] text-[#27B72D]' },
  INACTIVE: { title: 'Inactive', className: 'bg-white border-[#8F8F8F] text-[#8F8F8F]' },
  LOCKED: { title: 'Locked', className: 'bg-[#FEE6E6] border-[#F30808] text-[#F30808]' },
};

// * Role 스타일 정의
const userRoleStyles = {
  ROLE_USER: { title: 'USER', className: 'bg-[#E6F2FE] border-[#0890F3] text-[#0890F3]' },
  ROLE_ADMIN: { title: 'ADMIN', className: 'bg-[#FFF3E0] border-[#FF9800] text-[#FF9800]' },
};

interface Props {
  searchValue?: string;
}

const UserPermissionTab = ({ searchValue }: Props) => {
  
  // * 현재 로그인한 사용자 정보
  const currentUser = useSelector((state: RootState) => state.loginSlice.userInfo);
  
  const [data, setData] = useState<UserSearchResponse | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [sortField, setSortField] = useState<string>('createdDate');
  const [sortDirection, setSortDirection] = useState<string>('DESC');

  // * 체크박스 state
  const [checkedRows, setCheckedRows] = useState<number[]>([]);

  // * 권한 수정용 state 
  const [editingRoleUserId, setEditingRoleUserId] = useState<number | null>(null);
  const [roleEditValue, setRoleEditValue] = useState<string>(''); // USER, ADMIN
  
  // * Status 수정용 state
  const [editingStatusUserId, setEditingStatusUserId] = useState<number | null>(null);
  const [statusEditValue, setStatusEditValue] = useState<string>(''); // ACTIVE, INACTIVE

  // * 본인 권한 수정 경고 모달 state
  const [showSelfEditWarningModal, setShowSelfEditWarningModal] = useState(false);

  // * 사용자 조회 API 호출
  const getUsers = useCallback(async (showLoading: boolean = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const params = new URLSearchParams();
      params.append('page', (currentPage - 1).toString());
      params.append('size', '10');
      params.append('sort', `${sortField},${sortDirection}`);

      if (searchValue) {
        params.append('keyword', searchValue);
      }

      const { data } = await axios.get(`/api/admin/member/search?${params.toString()}`);

      // * API 응답
      const responseData = data?.data || data || {};
      const content = responseData.content || [];
      const totalPages = responseData.totalPages || 1;
      const totalElements = responseData.totalElements || 0;

      // * 페이징 정보 설정
      setTotalPages(totalPages);
      setTotalElements(totalElements);
      setData(responseData);
      console.log('Data set successfully:', responseData);
    } catch (error) {
      console.error('Users 조회 실패:', error);
      // * 에러 시 빈 데이터
      setData({
        content: [],
        totalPages: 1,
        totalElements: 0,
        pageable: {
          pageNumber: 0,
          pageSize: 10,
          sort: { empty: true, sorted: false, unsorted: true },
          offset: 0,
          paged: true,
          unpaged: false
        },
        last: true,
        size: 10,
        number: 0,
        sort: { empty: true, sorted: false, unsorted: true },
        first: true,
        numberOfElements: 0,
        empty: true
      });
      setTotalPages(1);
      setTotalElements(0);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  }, [currentPage, searchValue, sortField, sortDirection]);

  // * currentPage를 리셋
  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, sortField, sortDirection]);

  useEffect(() => {
    console.log('useEffect triggered - currentPage:', currentPage, 'searchValue:', searchValue, 'sortField:', sortField, 'sortDirection:', sortDirection);
    // * 초기 로딩
    const showLoading = isInitialLoad || data === null;
    getUsers(showLoading);
  }, [getUsers, isInitialLoad]);

  if (!data) return null;

  // * 권한 String <-> Number 매핑
  const roleToValue = (role: string): number => {
    const map: Record<string, number> = {
      ROLE_USER: 1,
      ROLE_ADMIN: 2,
    };
    return map[role] || 1;
  };
  const valueToRole = (value: number): string => {
    const map: Record<number, string> = {
      1: 'ROLE_USER',
      2: 'ROLE_ADMIN',
    };
    return map[value] || 'USER';
  };

  // * 권한 수정 함수 
  const updateUserRole = async ( memberId: number, newRole: string) => {
    try {
      if (newRole === 'ROLE_ADMIN') {
        await axios.post(`/api/admin/permission/grant-admin/${memberId}`);
      } else if (newRole === 'ROLE_USER') {
        await axios.delete(`/api/admin/permission/revoke-admin/${memberId}`);
      } else {
        console.log(`알 수 없는 Role: ${newRole}`);
      }
    } catch (error: any) {
      console.error('권한 변경 실패:', error);
      
      // * 마지막 관리자 제거 시도하면
      if (error.response?.data?.method === 'LAST_ADMIN_CANNOT_BE_REMOVED') { // * 백에서 에러 커스텀 
        alert('마지막 관리자는 제거할 수 없습니다.');
      } else {
        alert('권한 변경에 실패했습니다.');
      }
    }
  }

  // * Status 수정 함수
  const updateUserStatus = async (memberId: number) => {
    try {
      await axios.patch(`/api/admin/member/toggle-active/${memberId}`);
      // * 성공 시 새로고침
      getUsers();
    } catch (error) {
      console.error('상태 변경 실패:', error);
      alert('상태 변경에 실패했습니다.');
    }
  } 

  return (
    <>
      <Grid
        className="h-full max-w-full"
        gridColumns={userPermissionGridColumns}
        gridData={data?.content || []}
        searchValue={searchValue}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        checkedRows={checkedRows}
        setCheckedRows={setCheckedRows}
        options={{
          useFilter: false, 
          useCheckbox: true,
          useSort: {
            use: true,
            sortKeys: ['name', 'email', 'loginMethod', 'emailVerified', 'status', 'role']
          },
          usePagination: {
            use: true,
            maxPage: totalPages
          },
          customCell: {
            emailVerified: (_, row) => (
              <span>{row.emailVerified ? <BadgeCheckOutlineIcon className="w-6 h-6 [&_path]:stroke-green-600" /> : <XCircleOutlineIcon className="w-6 h-6 [&_path]:stroke-red-500" />}</span>
            ),
            role: (value, row) => {
              const user = row as UserItem;
              const role = userRoleStyles[user.role as keyof typeof userRoleStyles] || userRoleStyles.ROLE_USER;

              if (editingRoleUserId === user.memberId) {
                return (
                  <SelectBox
                    className="min-w-[120px] text-left"
                    value={roleToValue(roleEditValue)}
                    list={roleList}
                    label=""
                    setValue={async (newRoleValue) => {
                      const newRole = valueToRole(newRoleValue);
                      setRoleEditValue(newRole);

                      if (newRole !== user.role) {
                        await updateUserRole(user.memberId, newRole);
                        // * 새로고침
                        getUsers();
                      }

                      setEditingRoleUserId(null);
                    }}
                />
                );
              }

              return (
                <div
                  className={`${currentUser?.email === user.email ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  onClick={() => {
                    // * 자기 자신인 경우 클릭 방지
                    if (currentUser?.email === user.email) {
                      setShowSelfEditWarningModal(true);
                      return;
                    }
                    setEditingRoleUserId(user.memberId);
                    setRoleEditValue(user.role);
                  }}
                >
                  <div className={`flex h-6 w-fit items-center rounded-full border px-3 ${role.className}`}>
                    <span className="text-nowrap text-small font-regular">{role.title}</span>
                  </div>
                </div>
              )
            },
            status: (value, row) => {
              const user = row as UserItem;
              const status = userStatusStyles[user.status as keyof typeof userStatusStyles] || userStatusStyles.INACTIVE;
              
              return (
                <div className={`flex h-6 w-fit items-center rounded-full border px-3 ${status.className}`}>
                  <span className="text-nowrap text-small font-regular">{status.title}</span>
                </div>
              )
            }

          }
        }}
      />
      
      {/* 본인 권한 수정 경고 모달 */}
      <Modal isOpened={showSelfEditWarningModal}>
        <ModalInner className="md:w-[400px]" title="Warning" onClickClose={() => setShowSelfEditWarningModal(false)}>
          <p className="p-4 pt-0 text-body1">
            You cannot modify your own permissions.
          </p>
          <div className="flex w-full flex-row items-center justify-end bg-bg-transparent_gray_light p-4">
            <Button 
              className="h-9" 
              text="OK" 
              onClick={() => setShowSelfEditWarningModal(false)} 
              type="blue" 
              size="large" 
            />
          </div>
        </ModalInner>
      </Modal>
      
    </>
  );
};

export default UserPermissionTab;
